import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ShieldAlert, Loader2, AlertTriangle, PieChart, BarChart3, Save, History, Trash2, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Legend } from "recharts";
import { PortfolioFileImport, ImportedPosition } from "@/components/PortfolioFileImport";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { format } from "date-fns";

const COLORS = ["hsl(var(--primary))", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "hsl(var(--destructive))"];

interface SavedSnapshot {
  id: string;
  created_at: string;
  positions_count: number;
  total_value: number;
  positions_data: any[];
  analysis_data: any;
}

export default function RiskDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dbPositions, setDbPositions] = useState<any[]>([]);
  const [importedPositions, setImportedPositions] = useState<ImportedPosition[]>([]);
  const [showImport, setShowImport] = useState(false);
  const [savedSnapshots, setSavedSnapshots] = useState<SavedSnapshot[]>([]);
  const [saving, setSaving] = useState(false);
  const [dataSource, setDataSource] = useState<'db' | 'import'>('db');

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setUser(session?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setUser(session?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    loadData();
    loadSnapshots();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    const { data } = await supabase.from("positions").select("*").eq("user_id", user.id);
    setDbPositions(data || []);
    setLoading(false);
  };

  const loadSnapshots = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from("portfolio_snapshots")
      .select("*")
      .eq("user_id", user.id)
      .eq("snapshot_type", "risk")
      .order("created_at", { ascending: false })
      .limit(10);
    setSavedSnapshots((data as any[]) || []);
  }, [user]);

  const handleImport = (positions: ImportedPosition[]) => {
    setImportedPositions(positions);
    setDataSource('import');
    setShowImport(false);
  };

  const activePositions = dataSource === 'import' ? importedPositions : dbPositions;

  const riskMetrics = useMemo(() => {
    if (activePositions.length === 0) return null;

    const posData = activePositions.map((p: any) => {
      if (dataSource === 'import') {
        return { name: p.stockName.substring(0, 15), ticker: p.ticker, value: p.totalValue, gain: p.gainPct, currency: p.localCurrency || 'CHF' };
      }
      const value = p.total_value ? Number(p.total_value) : Number(p.buy_price) * Number(p.quantity);
      const gain = p.gain_pct ? Number(p.gain_pct) : 0;
      return { name: (p.stock_name || p.ticker || "").substring(0, 15), ticker: p.ticker, value, gain, currency: p.local_currency || "CHF" };
    });

    const totalValue = posData.reduce((sum, p) => sum + p.value, 0);
    const concentration = posData.map(p => ({ name: p.name, value: p.value, pct: totalValue > 0 ? (p.value / totalValue) * 100 : 0 })).sort((a, b) => b.pct - a.pct);
    const top3Concentration = concentration.slice(0, 3).reduce((sum, p) => sum + p.pct, 0);
    const gains = posData.map(p => p.gain * 100);
    const maxDrawdown = gains.length > 0 ? Math.min(...gains) : 0;
    const sortedGains = [...gains].sort((a, b) => a - b);
    const var95Index = Math.floor(gains.length * 0.05);
    const var95 = sortedGains[var95Index] || 0;
    const var95Amount = totalValue * (Math.abs(var95) / 100);

    const currencyMap = new Map<string, number>();
    posData.forEach(p => currencyMap.set(p.currency, (currencyMap.get(p.currency) || 0) + p.value));
    const currencyBreakdown = Array.from(currencyMap.entries()).map(([currency, value]) => ({ name: currency, value: Math.round(value), pct: Math.round((value / totalValue) * 100) })).sort((a, b) => b.value - a.value);

    const atRisk = posData.filter(p => p.gain < 0).length;
    const profitable = posData.filter(p => p.gain >= 0).length;

    let riskScore = 0;
    if (top3Concentration > 60) riskScore += 30; else if (top3Concentration > 40) riskScore += 15;
    if (Math.abs(maxDrawdown) > 30) riskScore += 25; else if (Math.abs(maxDrawdown) > 15) riskScore += 12;
    if (atRisk > activePositions.length * 0.5) riskScore += 20; else if (atRisk > activePositions.length * 0.3) riskScore += 10;
    if (currencyBreakdown.length === 1) riskScore += 15; else if (currencyBreakdown.length === 2) riskScore += 8;
    riskScore = Math.min(100, riskScore);

    return { totalValue, concentration, top3Concentration: Math.round(top3Concentration), maxDrawdown: Math.round(maxDrawdown * 100) / 100, var95: Math.round(var95 * 100) / 100, var95Amount: Math.round(var95Amount), currencyBreakdown, atRisk, profitable, positionCount: activePositions.length, riskScore };
  }, [activePositions, dataSource]);

  const saveSnapshot = async () => {
    if (!user || !riskMetrics) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("portfolio_snapshots").insert({
        user_id: user.id,
        snapshot_type: "risk",
        positions_data: activePositions as any,
        analysis_data: { ...riskMetrics, source: dataSource } as any,
        metadata: { source: dataSource } as any,
        positions_count: activePositions.length,
        total_value: riskMetrics.totalValue,
      });
      if (error) throw error;
      toast({ title: "Saved", description: "Risk analysis saved." });
      loadSnapshots();
    } catch (e: any) {
      toast({ title: "Save Failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const restoreSnapshot = (snap: SavedSnapshot) => {
    const analysis = snap.analysis_data as any;
    if (analysis?.source === 'import') {
      setImportedPositions(snap.positions_data as ImportedPosition[]);
      setDataSource('import');
    } else {
      setDbPositions(snap.positions_data);
      setDataSource('db');
    }
    toast({ title: "Restored", description: `Loaded from ${format(new Date(snap.created_at), "dd MMM yyyy HH:mm")}` });
  };

  const deleteSnapshot = async (id: string) => {
    await supabase.from("portfolio_snapshots").delete().eq("id", id);
    setSavedSnapshots(prev => prev.filter(s => s.id !== id));
    toast({ title: "Deleted" });
  };

  const riskLevel = (score: number) => {
    if (score <= 25) return { label: "Low", color: "text-emerald-500", bg: "bg-emerald-500" };
    if (score <= 50) return { label: "Moderate", color: "text-amber-500", bg: "bg-amber-500" };
    if (score <= 75) return { label: "High", color: "text-orange-500", bg: "bg-orange-500" };
    return { label: "Critical", color: "text-destructive", bg: "bg-destructive" };
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-destructive/10 rounded-lg">
                <ShieldAlert className="h-5 w-5 text-destructive" />
              </div>
              <h1 className="text-xl font-bold text-foreground">Risk Dashboard</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {riskMetrics && (
              <Button variant="outline" size="sm" onClick={saveSnapshot} disabled={saving} className="gap-2">
                <Save className="h-4 w-4" />{saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)} className="gap-2">
              <Upload className="h-4 w-4" />Import
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        <Collapsible open={showImport} onOpenChange={setShowImport}>
          <CollapsibleContent>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-base">Import Portfolio Data</CardTitle>
                <CardDescription>Upload Excel (PRVFIN) or IBKR file</CardDescription>
              </CardHeader>
              <CardContent>
                <PortfolioFileImport onImportComplete={handleImport} buttonLabel="Import for Risk Analysis" />
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {savedSnapshots.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4" /> Saved Analyses ({savedSnapshots.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {savedSnapshots.map(snap => (
                  <div key={snap.id} className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => restoreSnapshot(snap)} className="text-xs">
                      {format(new Date(snap.created_at), "dd MMM HH:mm")} • {snap.positions_count} pos
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteSnapshot(snap.id)}>
                      <Trash2 className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !riskMetrics ? (
          <Card className="text-center py-16">
            <CardContent>
              <ShieldAlert className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Position Data</h2>
              <p className="text-muted-foreground mb-4">Import positions to see risk analysis.</p>
              <Button onClick={() => setShowImport(true)} className="gap-2"><Upload className="h-4 w-4" />Import Data</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {dataSource === 'import' && <Badge variant="outline" className="text-xs">From imported file • {importedPositions.length} positions</Badge>}

            <Card className="border-2" style={{ borderColor: `var(--${riskLevel(riskMetrics.riskScore).bg === "bg-destructive" ? "destructive" : "border"})` }}>
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Portfolio Risk Score</p>
                  <div className="flex items-center gap-3">
                    <p className={`text-5xl font-bold ${riskLevel(riskMetrics.riskScore).color}`}>{riskMetrics.riskScore}</p>
                    <div>
                      <Badge className={`${riskLevel(riskMetrics.riskScore).bg} text-white`}>{riskLevel(riskMetrics.riskScore).label} Risk</Badge>
                      <p className="text-xs text-muted-foreground mt-1">Based on concentration, drawdown, currency & P&L</p>
                    </div>
                  </div>
                </div>
                <div className="w-32 h-32 relative">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="hsl(var(--muted))" strokeWidth="8" />
                    <circle cx="50" cy="50" r="40" fill="none" stroke={riskMetrics.riskScore <= 25 ? "#10b981" : riskMetrics.riskScore <= 50 ? "#f59e0b" : riskMetrics.riskScore <= 75 ? "#f97316" : "hsl(var(--destructive))"} strokeWidth="8" strokeDasharray={`${riskMetrics.riskScore * 2.51} 251`} strokeLinecap="round" />
                  </svg>
                  <span className={`absolute inset-0 flex items-center justify-center text-xl font-bold ${riskLevel(riskMetrics.riskScore).color}`}>{riskMetrics.riskScore}%</span>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">VaR (95%)</p><p className="text-xl font-bold text-destructive">{riskMetrics.var95}%</p><p className="text-[10px] text-muted-foreground">≈ {riskMetrics.var95Amount.toLocaleString()} CHF</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Max Drawdown</p><p className="text-xl font-bold text-destructive">{riskMetrics.maxDrawdown}%</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">Top 3 Concentration</p><p className={`text-xl font-bold ${riskMetrics.top3Concentration > 50 ? "text-amber-500" : "text-emerald-500"}`}>{riskMetrics.top3Concentration}%</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground mb-1">At Risk / Profitable</p><p className="text-xl font-bold text-foreground"><span className="text-destructive">{riskMetrics.atRisk}</span>{" / "}<span className="text-emerald-500">{riskMetrics.profitable}</span></p></CardContent></Card>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><PieChart className="h-4 w-4 text-primary" /> Portfolio Concentration</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPie>
                      <Pie data={riskMetrics.concentration.slice(0, 8)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, pct }) => `${name} ${Math.round(pct)}%`}>
                        {riskMetrics.concentration.slice(0, 8).map((_: any, i: number) => (<Cell key={i} fill={COLORS[i % COLORS.length]} />))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart3 className="h-4 w-4 text-amber-500" /> Currency Exposure</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={riskMetrics.currencyBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                      <XAxis dataKey="name" tick={{ fill: "hsl(var(--muted-foreground))" }} />
                      <YAxis tick={{ fill: "hsl(var(--muted-foreground))" }} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }} />
                      <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="Value" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Risk Warnings</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {riskMetrics.top3Concentration > 50 && (
                    <div className="flex items-start gap-3 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                      <div><p className="text-sm font-medium text-foreground">High Concentration Risk</p><p className="text-xs text-muted-foreground">Top 3 positions make up {riskMetrics.top3Concentration}% of your portfolio.</p></div>
                    </div>
                  )}
                  {Math.abs(riskMetrics.maxDrawdown) > 20 && (
                    <div className="flex items-start gap-3 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                      <div><p className="text-sm font-medium text-foreground">Significant Drawdown</p><p className="text-xs text-muted-foreground">Worst position down {Math.abs(riskMetrics.maxDrawdown)}%.</p></div>
                    </div>
                  )}
                  {riskMetrics.currencyBreakdown.length === 1 && (
                    <div className="flex items-start gap-3 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
                      <AlertTriangle className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                      <div><p className="text-sm font-medium text-foreground">Single Currency Exposure</p><p className="text-xs text-muted-foreground">All positions in {riskMetrics.currencyBreakdown[0]?.name}.</p></div>
                    </div>
                  )}
                  {riskMetrics.atRisk > riskMetrics.profitable && (
                    <div className="flex items-start gap-3 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                      <div><p className="text-sm font-medium text-foreground">Majority at Loss</p><p className="text-xs text-muted-foreground">{riskMetrics.atRisk} of {riskMetrics.positionCount} positions in the red.</p></div>
                    </div>
                  )}
                  {riskMetrics.riskScore <= 25 && (
                    <div className="flex items-start gap-3 p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                      <ShieldAlert className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                      <div><p className="text-sm font-medium text-foreground">Portfolio Looks Healthy</p><p className="text-xs text-muted-foreground">No major risk warnings detected.</p></div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
